import React, { PureComponent } from 'react';
import { layer, source, feature, interaction } from 'react-openlayers';
import GeometryType from 'ol/geom/GeometryType';
import { generateColor } from '../../../utils';
import { BLACK, WHITE } from '../../../rgbColors';
import { getCurrentMission } from '../../../dataModelHelpers';
import olFeature from 'ol/Feature';
import { Feature, Geometry, LineString } from 'geojson';
import { FIELD_ORDER_FEATURE, segmentArrow } from '../helpers/fieldOrder';
import { LocalAtRecord } from '../../../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { coordsFromGeoJsonFeature, parseGeoJsonText } from '../../../utilities/geojson-utils';

interface OperatorScheduleLayerProps {
  loadMissionSchedule: (jobId: string) => Promise<void>;
  scheduleFeatures: Feature<Geometry>[];
  missionActive: string;
  sampledItems: LocalAtRecord<JobsFieldSet>[];
  showSampledItems: boolean;
}

// The field order overlay rides above the boundaries it annotates.
const Z_INDEX_FIELD_ORDER = 2;

export default class OperatorScheduleLayer extends PureComponent<OperatorScheduleLayerProps> {
  FIELD_TRANSPARENCY = '40'; // 25% opacity
  SAMPLED_FIELD_COLOR = 'rgba(24, 126, 0, 0.74)';
  FIELD_ORDER_COLOR = '#ffffff';
  ORDER_BADGE_RADIUS = 20;
  fieldKeys: string[] = [];

  /**
   * Handle that calls and returns output from gen function based on type.
   * @param {object} scheduleFeature GeoJSON object
   * @returns {PolygonReact | MultiPolygonReact | PointReact} The react component generated
   */
  genScheduleFeature(scheduleFeature: Feature<Geometry>, idx: number) {
    const mission = getCurrentMission();

    switch (scheduleFeature.geometry.type) {
      case GeometryType.MULTI_POLYGON:
      case GeometryType.POLYGON:
        return (
          (!mission || mission.job_id !== scheduleFeature.properties?.['Rogo Job ID']) &&
          this.genFieldBoundary(scheduleFeature, idx)
        );

      case GeometryType.LINE_STRING:
        return scheduleFeature.properties?.name === FIELD_ORDER_FEATURE
          ? this.genFieldOrderHop(scheduleFeature, idx)
          : null;

      case GeometryType.POINT:
        if (scheduleFeature?.properties?.name === 'dropoff') {
          return this.genDropoffPoint(scheduleFeature);
        } else if (scheduleFeature.properties?.name === 'pullin') {
          return (
            (!mission || mission.job_id !== scheduleFeature.properties['Rogo Job ID']) &&
            this.genPullinPoint(scheduleFeature)
          );
        } else {
          return this.genInitialPoint(scheduleFeature);
        }

      default:
    }
  }

  /**
   * Generate operator schedule field boundary
   * @param {object} scheduleFeature GeoJSON object
   * @returns {MultiPolygonReact | PolygonReact} The react component generated
   */
  genFieldBoundary(scheduleFeature, idx) {
    const FieldFeature =
      scheduleFeature.geometry.type === GeometryType.MULTI_POLYGON ? feature.MultiPolygonReact : feature.PolygonReact;
    const fieldColor = generateColor(scheduleFeature.properties['Field from Form']);
    const fieldKey = `${scheduleFeature.properties['Field from Form']}-Field-${scheduleFeature.properties['Rogo Job ID']}`;
    const showName = !this.fieldKeys.includes(fieldKey);

    if (showName) {
      this.fieldKeys.push(fieldKey);
    }

    // A field wears its place in the shift once, on the part that carries the
    // name — the name lifts to make room for it.
    const order = scheduleFeature.properties['Shift Order'];
    const labelPoint = showName && order ? scheduleFeature.properties['Order Point'] : null;

    return (
      <React.Fragment key={`${fieldKey}-${idx}`}>
        {[
          <FieldFeature
            coordinates={scheduleFeature.geometry.coordinates}
            strokeOptions={{ color: fieldColor, width: 4 }}
            fillOptions={{ color: `${fieldColor}${this.FIELD_TRANSPARENCY}` }}
            // @ts-ignore
            textOptions={
              showName
                ? {
                    text: scheduleFeature.properties['Field from Form'],
                    font: '24px Calibri,sans-serif',
                    overflow: true,
                    offsetY: labelPoint ? -this.ORDER_BADGE_RADIUS - 12 : 0,
                  }
                : {}
            }
            hideTextZoom={12}
            key={`${scheduleFeature.properties['Field from Form']}-Field${idx}`}
            properties={scheduleFeature.properties}
          />,
          labelPoint && this.genOrderBadge(labelPoint, order, `${fieldKey}-order`),
        ]}
      </React.Fragment>
    );
  }

  /**
   * Generate the disc a field wears to show its place in the shift. Filled so
   * the number reads against imagery of any shade.
   */
  genOrderBadge(coordinate: number[], order: number, key: string) {
    return (
      <feature.PointReact
        key={key}
        coordinate={coordinate}
        circleOptions={{
          radius: this.ORDER_BADGE_RADIUS,
          fillOptions: { color: WHITE },
          strokeOptions: { color: BLACK, width: 2 },
        }}
        textOptions={{
          text: String(order),
          font: 'bold 26px Calibri,sans-serif',
          overflow: true,
          fillOptions: { color: BLACK },
        }}
        zIndex={Z_INDEX_FIELD_ORDER}
      />
    );
  }

  /**
   * Generate one hop of the shift's field order: the connector from one field's
   * number to the next one's, and an arrow showing which way it runs. The
   * numbers themselves are worn by the fields.
   * @param {object} scheduleFeature GeoJSON LineString built by fieldOrderFeatures
   * @returns {JSX.Element} The react components generated
   */
  genFieldOrderHop(scheduleFeature: Feature<Geometry>, idx: number) {
    if (scheduleFeature.geometry.type !== GeometryType.LINE_STRING) {
      return;
    }

    const [from, to] = (scheduleFeature.geometry as LineString).coordinates;
    const arrow = segmentArrow(from, to);
    const key = `field-order-${scheduleFeature.properties?.['fromOrder']}-${scheduleFeature.properties?.['toOrder']}-${idx}`;

    return (
      <React.Fragment key={key}>
        {[
          <feature.LineStringReact
            key={`${key}-line`}
            coordinates={(scheduleFeature.geometry as LineString).coordinates}
            strokeOptions={{ color: this.FIELD_ORDER_COLOR, width: 3, lineDash: [10, 6] }}
            zIndex={Z_INDEX_FIELD_ORDER}
          />,
          arrow && (
            <feature.PointReact
              key={`${key}-arrow`}
              coordinate={arrow.coordinate}
              textOptions={{
                text: '➤',
                font: 'bold 20px Calibri,sans-serif',
                strokeOptions: { color: BLACK, width: 3 },
                fillOptions: { color: WHITE },
                rotation: arrow.rotation,
              }}
              zIndex={Z_INDEX_FIELD_ORDER}
            />
          ),
        ]}
      </React.Fragment>
    );
  }

  genSampledItemFeature(item: LocalAtRecord<JobsFieldSet>) {
    const geoJsonText = item.get('Bnd GeoJSON Text');

    if (!geoJsonText || typeof geoJsonText !== 'string') {
      return null;
    }

    const geoJson = parseGeoJsonText(geoJsonText);

    if (!geoJson) {
      return null;
    }

    let featureAdded = false;

    return geoJson.features.map((geoJsonFeature, idx) => {
      const coords = coordsFromGeoJsonFeature(geoJsonFeature);

      if (!coords) {
        return null;
      }

      const geometryType = geoJsonFeature.geometry.type;

      if (geometryType !== GeometryType.POLYGON && geometryType !== GeometryType.MULTI_POLYGON) {
        return null;
      }

      const FieldFeature =
        geometryType === GeometryType.MULTI_POLYGON ? feature.MultiPolygonReact : feature.PolygonReact;

      const out = (
        <FieldFeature
          coordinates={coords as any}
          strokeOptions={{ color: this.SAMPLED_FIELD_COLOR, width: 4 }}
          fillOptions={{ color: `${this.SAMPLED_FIELD_COLOR}` }}
          // @ts-ignore
          textOptions={
            featureAdded
              ? {}
              : {
                  text: item.get('Field from Form') || 'unknown',
                  font: '24px Calibri,sans-serif',
                  overflow: true,
                }
          }
          hideTextZoom={12}
          key={`${item.get('Field from Form')}-Field${idx}`}
        />
      );

      featureAdded = true;

      return out;
    });
  }

  /**
   * Generate operator schedule dropoff point
   * @param {object} scheduleFeature GeoJSON object
   * @returns {PointReact} The react component generated
   */
  genDropoffPoint(scheduleFeature) {
    const dropoffColor = generateColor(scheduleFeature.properties['Dropoff Location']);

    return (
      <feature.PointReact
        coordinate={scheduleFeature.geometry.coordinates}
        circleOptions={{ radius: 6, fillOptions: { color: dropoffColor } }}
        key={`${scheduleFeature.properties['Dropoff Location']}-Dropoff`}
      />
    );
  }

  /**
   * Generate the initial point for the operator schedule field
   * @param {object} scheduleFeature GeoJSON object
   * @returns {PointReact} The react component generated
   */
  genInitialPoint(scheduleFeature) {}

  /**
   * Generate the pullin point for the operator schedule field
   * @param {object} scheduleFeature GeoJSON object
   * @returns {PointReact} The react component generated
   */
  genPullinPoint(scheduleFeature) {
    const pullinColor = generateColor(scheduleFeature.properties['Field from Form']);

    return (
      <feature.PointReact
        coordinate={scheduleFeature.geometry.coordinates}
        circleOptions={{ radius: 6, fillOptions: { color: pullinColor }, strokeOptions: { width: 1, color: WHITE } }}
        key={`${scheduleFeature.properties['Field from Form']}-Pullin`}
      />
    );
  }

  /**
   * Select handle that allows user to select a field to load from the map
   * @param {*} event
   */
  async handleSelectFeature(feature: olFeature) {
    console.log('feat', feature);

    if (feature) {
      const properties = feature.getProperties();
      console.log('PROPERTIES FIELD', properties.name);

      if (properties.name === 'field') {
        await this.props.loadMissionSchedule(properties['Rogo Job ID']);
      }
    }

    return false;
  }

  render() {
    this.fieldKeys = [];

    return (
      // @ts-ignore
      <layer.VectorImage zIndex={1}>
        {/*@ts-ignore */}
        <source.VectorSourceReact>
          {this.props.scheduleFeatures.map((feature, idx) => this.genScheduleFeature(feature, idx))}
          {this.props.showSampledItems && this.props.sampledItems.map((item) => this.genSampledItemFeature(item))}
          {!Boolean(this.props.missionActive) && (
            <interaction.SelectReact
              // @ts-ignore
              filter={this.handleSelectFeature.bind(this)}
              style={null}
            />
          )}
        </source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
